import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  EDITABLE_STATUSES,
  LOSS_TYPE_OPTIONS,
  PAYMENT_MODE_OPTIONS,
  isCashPayment,
  resolveApprovalStatus,
} from "./constants";
import * as api from "./billCollection.service";

const CollectionUpdation = ({ appointmentId, leadId, collectionStatus, paymentMode }) => {
  const [loading, setLoading] = useState(true);
  const [collectableAmount, setCollectableAmount] = useState(0);
  const [collectedAtHospital, setCollectedAtHospital] = useState(0);
  const [originalCollectedAtHospital, setOriginalCollectedAtHospital] = useState(0);
  const [ghvTransactions, setGhvTransactions] = useState([]);
  const [extraCashRows, setExtraCashRows] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState("collectible");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [daDocuments, setDaDocuments] = useState([]);
  const [gapReasons, setGapReasons] = useState([]);
  const [paymentHistoryLoaded, setPaymentHistoryLoaded] = useState(false);
  const [daDocsLoaded, setDaDocsLoaded] = useState(false);
  const [gapReasonsLoaded, setGapReasonsLoaded] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveRemarks, setApproveRemarks] = useState("");
  const [lossType, setLossType] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isFormDisabled = collectionStatus && !EDITABLE_STATUSES.includes(collectionStatus);
  const isCollectibleZero = collectableAmount === 0;
  const isCash = isCashPayment(paymentMode);

  const totalGhvCollected = ghvTransactions.reduce(
    (sum, t) => sum + (Number(t.amount) || 0),
    0
  );
  const collectionGapRaw = collectableAmount - collectedAtHospital - totalGhvCollected;
  const hasExcess = collectionGapRaw < 0;
  const excessAmount = hasExcess ? Math.abs(collectionGapRaw) : 0;
  const hasLoss = collectionGapRaw > 0;

  const fetchDetails = useCallback(async () => {
    if (!appointmentId) return;
    setLoading(true);
    try {
      const res = await api.getCollectionDetails(appointmentId);
      const d = res?.data || res || {};
      setCollectableAmount(d.collectableAmount || 0);
      setCollectedAtHospital(d.collectedAtHospital || 0);
      setOriginalCollectedAtHospital(d.collectedAtHospital || 0);
      setExtraCashRows(d.extraCashProof || []);
      const ghv = (d.collectedAtGHV || [])
        .filter((g) => !g.isDeleted)
        .map((g) => ({
          id: g.id,
          amount: g.amount,
          paymentMode: g.paymentMode,
          transactionId: g.transactionId,
          _original: {
            amount: g.amount,
            paymentMode: g.paymentMode,
            transactionId: g.transactionId,
          },
        }));
      setGhvTransactions(ghv);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load collection details");
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const loadPaymentHistory = async () => {
    if (paymentHistoryLoaded) return;
    try {
      const res = await api.getPaymentHistory(appointmentId);
      setPaymentHistory(res?.data || []);
    } catch {
      toast.error("Failed to load payment history");
    } finally {
      setPaymentHistoryLoaded(true);
    }
  };

  const loadDaDocs = async () => {
    if (daDocsLoaded) return;
    try {
      const res = await api.getDaDocuments(leadId);
      setDaDocuments(res?.data || []);
    } catch {
      toast.error("Failed to load DA documents");
    } finally {
      setDaDocsLoaded(true);
    }
  };

  const loadGapReasons = async () => {
    if (gapReasonsLoaded) return;
    try {
      const res = await api.getCollectionGapReason(appointmentId);
      setGapReasons(Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : []);
    } catch {
      toast.error("Failed to load gap reasons");
    } finally {
      setGapReasonsLoaded(true);
    }
  };

  const addGhvRow = () => {
    setGhvTransactions((prev) => [
      ...prev,
      { amount: "", paymentMode: "UPI", transactionId: "" },
    ]);
  };

  const updateGhvRow = (index, field, value) => {
    setGhvTransactions((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const removeGhvRow = (index) => {
    setGhvTransactions((prev) => prev.filter((_, i) => i !== index));
  };

  const buildSavePayload = (status, remarks, selectedLossType, collectionLoss) => {
    const payload = {
      appointmentId,
      collectionStatus: { status, remarks, source: "Central Dashboard" },
    };
    if (selectedLossType) {
      payload.collectionLoss = {
        amount: collectionLoss,
        type: selectedLossType,
        remarks,
      };
    }
    if (collectedAtHospital !== originalCollectedAtHospital) {
      payload.collectedAtHospital = {
        amount: collectedAtHospital,
        source: "Central Dashboard",
      };
    }
    const changedGhv = ghvTransactions.filter((t) => {
      if (!t.id) return true; // new
      const orig = t._original;
      return (
        orig &&
        (String(t.amount) !== String(orig.amount) ||
          t.paymentMode !== orig.paymentMode ||
          t.transactionId !== orig.transactionId)
      );
    });
    if (changedGhv.length) {
      payload.collectedAtGHV = changedGhv.map((t) => ({
        ...(t.id ? { id: t.id } : {}),
        paymentMode: t.paymentMode,
        transactionId: t.transactionId,
        amount: Number(t.amount),
      }));
    }
    return payload;
  };

  const handleApprove = async () => {
    const status = resolveApprovalStatus(hasLoss, isCash);
    const collectionLoss = hasLoss ? collectionGapRaw : 0;
    setSubmitting(true);
    try {
      const payload = buildSavePayload(status, approveRemarks, lossType, collectionLoss);
      await api.saveCollectionEntry(payload);
      toast.success("Collection entry saved");
      setApproveModalOpen(false);
      fetchDetails();
    } catch {
      toast.error("Failed to save collection entry");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="cbc-loading">
        <CircularProgress size={24} />
      </div>
    );
  }

  return (
    <div>
      {/* Status pill */}
      <div style={{ marginBottom: 12 }}>
        <span className={`cbc-pill ${isFormDisabled ? "red" : "green"}`}>
          {collectionStatus || "N/A"}
        </span>
        {isCollectibleZero && (
          <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>
            Bill Approve Pending At Finance.
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="cbc-tabs" style={{ marginBottom: 12 }}>
        {[
          { key: "collectible", label: "Collectible Amount" },
          { key: "extraCash", label: "Extra Cash Proof" },
          { key: "paymentHistory", label: "Payment History" },
          { key: "daDocs", label: "DA Document" },
          { key: "gapRemarks", label: "Gap Remarks" },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={`cbc-tab ${activeSubTab === key ? "active" : ""}`}
            onClick={() => {
              setActiveSubTab(key);
              if (key === "paymentHistory") loadPaymentHistory();
              if (key === "daDocs") loadDaDocs();
              if (key === "gapRemarks") loadGapReasons();
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Collectible Amount tab */}
      {activeSubTab === "collectible" && (
        <div>
          <div className="cbc-form-grid" style={{ marginBottom: 12 }}>
            <div>
              <label className="cbc-label">Collectible Amount</label>
              <input className="cbc-input" value={collectableAmount} readOnly disabled />
            </div>
            <div>
              <label className="cbc-label">Collected at Hospital</label>
              <input
                type="number"
                className="cbc-input"
                value={collectedAtHospital}
                onChange={(e) => setCollectedAtHospital(Number(e.target.value) || 0)}
                disabled={isFormDisabled || isCollectibleZero}
              />
            </div>
            <div>
              <label className="cbc-label">Total GHV Collected</label>
              <input className="cbc-input" value={totalGhvCollected} readOnly disabled />
            </div>
            <div>
              <label className="cbc-label">
                {hasExcess ? "Excess Amount" : "Collection Gap"}
              </label>
              <input
                className="cbc-input"
                value={hasExcess ? excessAmount : Math.max(collectionGapRaw, 0)}
                readOnly
                disabled
                style={{ color: hasExcess ? "#059669" : collectionGapRaw > 0 ? "#dc2626" : undefined }}
              />
            </div>
          </div>

          {/* GHV Transactions */}
          <div className="cbc-section-title">GHV Transactions</div>
          <div className="cbc-table-wrap">
            <table className="cbc-table">
              <thead>
                <tr>
                  <th>Payment Mode</th>
                  <th>Transaction ID</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {ghvTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", color: "#9ca3af" }}>
                      No GHV transactions
                    </td>
                  </tr>
                ) : (
                  ghvTransactions.map((t, i) => (
                    <tr key={i}>
                      <td>
                        <select
                          className="cbc-select"
                          value={t.paymentMode}
                          onChange={(e) => updateGhvRow(i, "paymentMode", e.target.value)}
                          disabled={isFormDisabled || isCollectibleZero}
                          style={{ minHeight: 36 }}
                        >
                          {PAYMENT_MODE_OPTIONS.map((pm) => (
                            <option key={pm} value={pm}>{pm}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className="cbc-input"
                          value={t.transactionId}
                          onChange={(e) => updateGhvRow(i, "transactionId", e.target.value)}
                          disabled={isFormDisabled || isCollectibleZero}
                          style={{ minHeight: 36 }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="cbc-input"
                          value={t.amount}
                          onChange={(e) => updateGhvRow(i, "amount", e.target.value)}
                          disabled={isFormDisabled || isCollectibleZero}
                          style={{ minHeight: 36 }}
                        />
                      </td>
                      <td>
                        {!t.id && (
                          <button
                            className="cbc-btn danger"
                            style={{ padding: "2px 8px", minHeight: 28, fontSize: 11 }}
                            onClick={() => removeGhvRow(i)}
                            disabled={isFormDisabled}
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!isFormDisabled && !isCollectibleZero && (
            <button
              className="cbc-btn outline"
              style={{ marginTop: 8, fontSize: 12 }}
              onClick={addGhvRow}
            >
              + Add GHV Transaction
            </button>
          )}

          {/* Send for Approval */}
          {!isFormDisabled && !isCollectibleZero && (
            <div style={{ marginTop: 16 }}>
              <button
                className="cbc-btn primary full-mobile"
                onClick={() => setApproveModalOpen(true)}
              >
                Send for Approval
              </button>
            </div>
          )}
        </div>
      )}

      {/* Extra Cash Proof tab */}
      {activeSubTab === "extraCash" && (
        <div>
          {extraCashRows.length === 0 ? (
            <div className="cbc-empty">No extra cash proof records</div>
          ) : (
            <div className="cbc-table-wrap">
              <table className="cbc-table">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>At Time Of</th>
                    <th>Collected By</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {extraCashRows.map((row, i) => (
                    <tr key={i}>
                      <td>{row.amount}</td>
                      <td>{row.atTheTimeOf}</td>
                      <td>{row.collectedBy}</td>
                      <td>
                        <span className={`cbc-pill ${row.status === "Approved" ? "green" : "yellow"}`}>
                          {row.status || "N/A"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Payment History tab */}
      {activeSubTab === "paymentHistory" && (
        <div>
          {!paymentHistoryLoaded ? (
            <div className="cbc-loading"><CircularProgress size={20} /></div>
          ) : paymentHistory.length === 0 ? (
            <div className="cbc-empty">No payment history</div>
          ) : (
            <div className="cbc-table-wrap">
              <table className="cbc-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Invoice</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((row) => (
                    <tr key={row._id}>
                      <td>{row.vendor}</td>
                      <td>₹{row.amount}</td>
                      <td>
                        <span className={`cbc-pill ${row.status?.toLowerCase() === "success" ? "green" : "yellow"}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>{row.invoiceNumber || "-"}</td>
                      <td>{row._created_at ? new Date(row._created_at).toLocaleDateString("en-IN") : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* DA Document tab */}
      {activeSubTab === "daDocs" && (
        <div>
          {!daDocsLoaded ? (
            <div className="cbc-loading"><CircularProgress size={20} /></div>
          ) : daDocuments.length === 0 ? (
            <div className="cbc-empty">No DA documents</div>
          ) : (
            <div className="cbc-table-wrap">
              <table className="cbc-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Loan Amount</th>
                    <th>EMI Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {daDocuments.map((row, i) => (
                    <tr key={i}>
                      <td>{row.vendor}</td>
                      <td>₹{row.loanAmount}</td>
                      <td>{row.emiStatus}</td>
                      <td>{row.date || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Gap Remarks tab */}
      {activeSubTab === "gapRemarks" && (
        <div>
          {!gapReasonsLoaded ? (
            <div className="cbc-loading"><CircularProgress size={20} /></div>
          ) : gapReasons.length === 0 ? (
            <div className="cbc-empty">No gap remarks</div>
          ) : (
            gapReasons.map((r, i) => (
              <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{r.value}</div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                  {r.actionTakenByUser} — {r.dateTimeIso ? new Date(r.dateTimeIso).toLocaleString("en-IN") : ""}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Approve Dialog */}
      <Dialog open={approveModalOpen} onClose={() => setApproveModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Send for Approval
          <IconButton onClick={() => setApproveModalOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 12 }}>
            <div className="cbc-form-grid">
              <div>
                <label className="cbc-label">Collectible</label>
                <input className="cbc-input" value={collectableAmount} readOnly disabled />
              </div>
              <div>
                <label className="cbc-label">Hospital</label>
                <input className="cbc-input" value={collectedAtHospital} readOnly disabled />
              </div>
              <div>
                <label className="cbc-label">GHV</label>
                <input className="cbc-input" value={totalGhvCollected} readOnly disabled />
              </div>
              <div>
                <label className="cbc-label">Gap</label>
                <input className="cbc-input" value={Math.max(collectionGapRaw, 0)} readOnly disabled />
              </div>
            </div>
          </div>
          {hasLoss && (
            <div style={{ marginBottom: 12 }}>
              <label className="cbc-label">Loss Type</label>
              <select
                className="cbc-select"
                value={lossType}
                onChange={(e) => setLossType(e.target.value)}
              >
                <option value="">Select loss type</option>
                {LOSS_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label className="cbc-label">Remarks</label>
            <textarea
              className="cbc-input"
              rows={3}
              value={approveRemarks}
              onChange={(e) => setApproveRemarks(e.target.value)}
              placeholder="Enter remarks..."
              style={{ resize: "vertical", minHeight: 80 }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button className="cbc-btn outline" onClick={() => setApproveModalOpen(false)}>
              Cancel
            </button>
            <button className="cbc-btn primary" onClick={handleApprove} disabled={submitting}>
              {submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Approve"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollectionUpdation;
